import type { Summary } from './api';
import type { Seccion } from './menu';

/**
 * Lo que dice la bienvenida de la portada.
 *
 * Quien entraba caía en frío en cinco cifras. Ahora la portada saluda a la
 * persona por su nombre, dice en qué negocio está y, en una línea, lo que pide
 * atención hoy. Todo sale del resumen que la portada ya carga: no se pregunta
 * nada nuevo al servidor para saludar.
 *
 * Reglas puras, para poder probarlas sin pantalla.
 */

export type Momento = 'dias' | 'tardes' | 'noches';

const SALUDOS: Record<Momento, string> = {
  dias: 'Buenos días',
  tardes: 'Buenas tardes',
  noches: 'Buenas noches',
};

/**
 * La hora de Lima, no la del equipo: el almacén se abre desde un teléfono que
 * puede estar en otra zona, y «buenas noches» a las diez de la mañana del
 * local se lee como un error.
 */
export function momentoEnLima(ahora: Date): Momento {
  const hora = Number(
    new Intl.DateTimeFormat('es-PE', {
      timeZone: 'America/Lima',
      hour: 'numeric',
      hourCycle: 'h23',
    }).format(ahora),
  );

  if (hora >= 5 && hora < 12) return 'dias';
  if (hora >= 12 && hora < 19) return 'tardes';
  return 'noches';
}

/** «Juana» de «Juana Mendoza»: se saluda a la persona, no a su ficha. */
export function primerNombre(nombreCompleto: string): string {
  return nombreCompleto.trim().split(/\s+/)[0] ?? '';
}

/**
 * Si el negocio todavía no ha registrado lo que tiene.
 *
 * Valorizado en cero con productos dados de alta: nada ha entrado nunca por
 * compra, carga ni conteo. Es lo normal en quien empezó vendiendo con el
 * celular, y entonces «bajo mínimo» no dice nada todavía: lo útil es invitarlo
 * a empezar. Se mira el texto del importe y no un número: el dinero no pasa por
 * coma flotante.
 */
export function nuncaContado(
  resumen: Pick<Summary, 'totalValue' | 'activeSupplies' | 'stockRecorded'>,
): boolean {
  // Lo dice el servidor cuando lo sabe (`stockRecorded`): es la misma regla que
  // pinta la tarjeta «Todavía no registraste» de la portada, y con dos reglas
  // la bienvenida y la tarjeta se contradecían. El importe en cero queda para
  // un servidor de antes, que no manda el dato.
  if (resumen.stockRecorded !== undefined) {
    return resumen.activeSupplies > 0 && !resumen.stockRecorded;
  }
  return resumen.activeSupplies > 0 && !/[1-9]/.test(resumen.totalValue);
}

export interface Destino {
  texto: string;
  seccion: Seccion;
}

export interface Bienvenida {
  saludo: string;
  /** Lo que pide atención hoy, lo más grave primero. Vacío: todo en orden. */
  pendientes: Destino[];
  /** Por dónde empezar, si el negocio nunca registró lo que tiene. */
  invitacion: Destino[] | null;
}

const PARA_EMPEZAR: readonly Destino[] = [
  { texto: 'Cargar lo que tienes de una hoja', seccion: 'carga-inicial' },
  { texto: 'Registrar lo que llega', seccion: 'recepcion' },
  { texto: 'Contar el estante', seccion: 'conteo' },
];

export function bienvenidaDe(datos: {
  nombre: string;
  ahora: Date;
  resumen: Summary;
  /** Las secciones que esta persona ve en su menú. */
  secciones: readonly Seccion[];
  /** «insumos» o «productos», del vocabulario del rubro. */
  insumos: string;
}): Bienvenida {
  const { resumen, secciones } = datos;
  const nombre = primerNombre(datos.nombre);
  const saludo = `${SALUDOS[momentoEnLima(datos.ahora)]}${nombre === '' ? '' : `, ${nombre}`}`;
  const sinContar = nuncaContado(resumen);

  const pendientes: Destino[] = [];

  if (resumen.expired > 0) {
    pendientes.push({
      texto:
        resumen.expired === 1
          ? '1 lote vencido espera su baja'
          : `${resumen.expired} lotes vencidos esperan su baja`,
      seccion: 'lotes',
    });
  }

  if (resumen.expiringSoon > 0) {
    pendientes.push({
      texto:
        resumen.expiringSoon === 1
          ? '1 lote vence en los próximos días'
          : `${resumen.expiringSoon} lotes vencen en los próximos días`,
      seccion: 'lotes',
    });
  }

  // Sin nada registrado, «bajo mínimo» son todos: la invitación de abajo dice
  // lo mismo, y mejor.
  if (!sinContar && resumen.belowMinimum > 0) {
    pendientes.push({
      texto: `${resumen.belowMinimum} ${datos.insumos} bajo su mínimo`,
      seccion: 'reposicion',
    });
  }

  const invitacion = sinContar
    ? PARA_EMPEZAR.filter((destino) => secciones.includes(destino.seccion))
    : null;

  return {
    saludo,
    pendientes: pendientes.filter((destino) => secciones.includes(destino.seccion)),
    invitacion: invitacion !== null && invitacion.length > 0 ? invitacion : null,
  };
}
